const DEFAULT_CONFIG = {
  slowSpeedThresholdKbps: 512,
  stalledTimeoutMs: 15000,
  rollingWindowMs: 10000,
  slowSpeedGraceMs: 5000,
};

// Push-based subscribers (see subscribe/notify) only receive updates when something the service
// does triggers a notify(). But rollingBps DECAYS with wall-clock time — if no bytes have
// flowed for rollingWindowMs, the truthful reading is 0, and the UI needs to know that
// without waiting for the (never-arriving) next recordProgress call. The decay tick is a
// low-cost interval that recomputes rollingBps for active sessions and, if the value
// materially changed, re-notifies subscribers. 2 s matches the stall-watchdog poll cadence
// in ChunkedUploadService so both signals refresh in lockstep.
const DECAY_TICK_MS = 2_000;
// Ignore sub-KB/s noise so we don't spam re-renders as the last few samples age out.
const DECAY_NOTIFY_EPSILON_BPS = 1024;

const STATUS = {
  UPLOADING: "uploading",
  WAITING: "waiting",
  SLOW: "slow",
  STALLED: "stalled",
  PAUSED: "paused",
  FINALIZING: "finalizing",
  COMPLETED: "completed",
  FAILED: "failed",
};

const sessions = new Map();
const listeners = new Map();
let decayTickHandle = null;

const isActiveStatus = (status) =>
  status === STATUS.UPLOADING ||
  status === STATUS.SLOW ||
  status === STATUS.STALLED ||
  status === STATUS.FINALIZING;

// Refresh rollingBps for every active session; notify subscribers of sessions whose value
// materially changed. Only runs while at least one active session exists — see
// ensureDecayTick / maybeStopDecayTick for the lifecycle.
const runDecayTick = () => {
  const now = Date.now();
  sessions.forEach((session) => {
    if (!isActiveStatus(session.status)) return;
    const previous = session.rollingBps || 0;
    applyRollingSpeed(session, now);
    const next = session.rollingBps || 0;
    if (Math.abs(next - previous) >= DECAY_NOTIFY_EPSILON_BPS) {
      notify(session.uploadId);
    }
  });
  maybeStopDecayTick();
};

const ensureDecayTick = () => {
  if (decayTickHandle !== null) return;
  // Bail out gracefully in non-browser environments (jest jsdom is fine; ssr / node isn't).
  if (typeof setInterval !== "function") return;
  decayTickHandle = setInterval(runDecayTick, DECAY_TICK_MS);
};

const maybeStopDecayTick = () => {
  if (decayTickHandle === null) return;
  let hasActive = false;
  sessions.forEach((session) => {
    if (isActiveStatus(session.status)) hasActive = true;
  });
  if (!hasActive) {
    clearInterval(decayTickHandle);
    decayTickHandle = null;
  }
};

const notify = (uploadId) => {
  const state = uploadTelemetryService.getState(uploadId);
  if (!state) {
    return;
  }
  const subs = listeners.get(uploadId);
  if (subs) {
    subs.forEach((listener) => listener(state));
  }
};

const clearStalledTimer = (session) => {
  if (session.stalledTimer) {
    clearTimeout(session.stalledTimer);
    session.stalledTimer = null;
  }
};

const scheduleStalledTimer = (session) => {
  clearStalledTimer(session);
  session.stalledTimer = setTimeout(() => {
    if (
      session.status === STATUS.COMPLETED ||
      session.status === STATUS.FAILED
    ) {
      return;
    }
    session.status = STATUS.STALLED;
    notify(session.uploadId);
  }, session.config.stalledTimeoutMs);
};

const ensureSession = (uploadId) => {
  const session = sessions.get(uploadId);
  if (!session) {
    throw new Error(`No telemetry session for upload ${uploadId}`);
  }
  return session;
};

const applyRollingSpeed = (session, now) => {
  const samples = session.samples;
  const cutoff = now - session.config.rollingWindowMs;
  while (samples.length > 1 && samples[0].timestamp < cutoff) {
    samples.shift();
  }
  const first = samples[0];
  const last = samples[samples.length - 1];
  if (!first || !last) {
    session.rollingBps = session.averageBps;
    return;
  }
  // Truthful "current" speed: if the most recent sample is older than the rolling window,
  // the client has stopped seeing bytes flow — the display must reflect that as 0 KB/s,
  // NOT the pre-stall value. Prior to this branch, rollingBps was only recomputed inside
  // recordProgress(); when a zombie TCP silently killed onProgress fires, the value stayed
  // frozen at whatever it was pre-stall, and the UI kept saying "500 KB/s" while the
  // stall watchdog was firing. That mismatch is what produced the "upload is going, but
  // says press retry" confusion reported 2026-07-31. See STUCK_UPLOAD_RELOAD_HYPOTHESIS.md
  // for the full trace.
  if (last.timestamp < cutoff) {
    session.rollingBps = 0;
    return;
  }
  const durationMs = last.timestamp - first.timestamp;
  if (durationMs <= 0) {
    session.rollingBps = session.averageBps;
    return;
  }
  session.rollingBps =
    (last.loaded - first.loaded) / (durationMs / 1000);
};

export const uploadTelemetryService = {
  STATUS,
  start(uploadId, { totalBytes, config } = {}) {
    if (!uploadId) {
      throw new Error("uploadId is required");
    }
    const merged = {
      ...DEFAULT_CONFIG,
      ...(config || {}),
    };
    const session = {
      uploadId,
      totalBytes,
      startTime: Date.now(),
      uploadedBytes: 0,
      elapsedTime: 0,
      averageBps: 0,
      rollingBps: 0,
      status: STATUS.UPLOADING,
      samples: [],
      config: {
        ...merged,
        slowSpeedThresholdBps: merged.slowSpeedThresholdKbps * 1024,
        slowSpeedGraceMs: merged.slowSpeedGraceMs,
      },
      stalledTimer: null,
    };
    sessions.set(uploadId, session);
    scheduleStalledTimer(session);
    ensureDecayTick();
    notify(uploadId);
  },

  // Boot-time helper for rehydrating a paused upload after page reload. Creates a session
  // already in PAUSED state with the persisted byte counts so the card shows "X of Y MB"
  // immediately instead of flashing zeros until the user re-selects files.
  seedPaused(uploadId, { totalBytes, uploadedBytes } = {}) {
    if (!uploadId || sessions.has(uploadId)) return;
    const merged = { ...DEFAULT_CONFIG };
    const session = {
      uploadId,
      totalBytes: totalBytes || 0,
      startTime: Date.now(),
      uploadedBytes: uploadedBytes || 0,
      elapsedTime: 0,
      averageBps: 0,
      rollingBps: 0,
      status: STATUS.PAUSED,
      samples: [],
      config: {
        ...merged,
        slowSpeedThresholdBps: merged.slowSpeedThresholdKbps * 1024,
        slowSpeedGraceMs: merged.slowSpeedGraceMs,
      },
      stalledTimer: null,
      pausedAt: Date.now(),
    };
    sessions.set(uploadId, session);
    notify(uploadId);
  },

  // Hold telemetry in WAITING while a package is queued behind the active one. The stall timer
  // is stopped so the UI doesn't briefly flip to "appears stalled" before the slot opens; the
  // first recordProgress after acquire transitions back to UPLOADING.
  markWaiting(uploadId) {
    const session = sessions.get(uploadId);
    if (!session) return;
    if (
      session.status === STATUS.COMPLETED ||
      session.status === STATUS.FAILED ||
      session.status === STATUS.FINALIZING
    ) {
      return;
    }
    clearStalledTimer(session);
    session.status = STATUS.WAITING;
    notify(uploadId);
  },

  recordProgress(uploadId, { loaded } = {}) {
    const session = ensureSession(uploadId);
    if (
      session.status === STATUS.COMPLETED ||
      session.status === STATUS.FAILED ||
      session.status === STATUS.PAUSED
    ) {
      return;
    }
    if (session.status === STATUS.WAITING) {
      // Slot opened; reset the startTime so averageBps doesn't get skewed by the queue wait.
      session.startTime = Date.now();
    }
    const now = Date.now();
    session.uploadedBytes = loaded;
    session.elapsedTime = (now - session.startTime) / 1000;
    session.averageBps =
      session.elapsedTime > 0 ? loaded / session.elapsedTime : 0;
    session.samples.push({ timestamp: now, loaded });
    applyRollingSpeed(session, now);

    let nextStatus = STATUS.UPLOADING;
    if (session.status === STATUS.STALLED) {
      nextStatus = STATUS.UPLOADING;
    }
    const hasExitedGrace =
      now - session.startTime >= session.config.slowSpeedGraceMs;
    if (
      hasExitedGrace &&
      session.rollingBps > 0 &&
      session.rollingBps < session.config.slowSpeedThresholdBps
    ) {
      nextStatus = STATUS.SLOW;
    }
    session.status = nextStatus;
    scheduleStalledTimer(session);
    notify(uploadId);
  },

  // User-initiated pause: stop the stall timer so the UI doesn't flip to "appears stalled"
  // during the gap, and drop sample history so post-resume rolling speed reflects fresh
  // throughput instead of being skewed by the paused window.
  pause(uploadId) {
    const session = sessions.get(uploadId);
    if (!session) return;
    if (
      session.status === STATUS.COMPLETED ||
      session.status === STATUS.FAILED ||
      session.status === STATUS.FINALIZING
    ) {
      return;
    }
    clearStalledTimer(session);
    session.samples = [];
    session.rollingBps = 0;
    session.pausedAt = Date.now();
    session.status = STATUS.PAUSED;
    notify(uploadId);
  },

  resume(uploadId) {
    const session = sessions.get(uploadId);
    if (!session || session.status !== STATUS.PAUSED) return;
    if (session.pausedAt) {
      // Advance startTime by the paused duration so averageBps reflects active-only time.
      session.startTime += Date.now() - session.pausedAt;
      session.pausedAt = null;
    }
    session.status = STATUS.UPLOADING;
    scheduleStalledTimer(session);
    ensureDecayTick();
    notify(uploadId);
  },

  markFinalizing(uploadId) {
    const session = sessions.get(uploadId);
    if (!session) {
      return;
    }
    if (
      session.status === STATUS.COMPLETED ||
      session.status === STATUS.FAILED
    ) {
      return;
    }
    session.uploadedBytes = session.totalBytes || session.uploadedBytes;
    session.elapsedTime = (Date.now() - session.startTime) / 1000;
    session.status = STATUS.FINALIZING;
    clearStalledTimer(session);
    notify(uploadId);
  },

  complete(uploadId) {
    const session = sessions.get(uploadId);
    if (!session) {
      return;
    }
    session.uploadedBytes = session.totalBytes || session.uploadedBytes;
    session.elapsedTime = (Date.now() - session.startTime) / 1000;
    session.status = STATUS.COMPLETED;
    clearStalledTimer(session);
    notify(uploadId);
    maybeStopDecayTick();
  },

  fail(uploadId) {
    const session = sessions.get(uploadId);
    if (!session) {
      return;
    }
    session.status = STATUS.FAILED;
    clearStalledTimer(session);
    notify(uploadId);
    maybeStopDecayTick();
  },

  subscribe(uploadId, listener) {
    if (!listener) {
      return () => {};
    }
    const set = listeners.get(uploadId) || new Set();
    set.add(listener);
    listeners.set(uploadId, set);
    const state = this.getState(uploadId);
    if (state) {
      listener(state);
    }
    return () => {
      const next = listeners.get(uploadId);
      if (!next) {
        return;
      }
      next.delete(listener);
      if (next.size === 0) {
        listeners.delete(uploadId);
      }
    };
  },

  getState(uploadId) {
    const session = sessions.get(uploadId);
    if (!session) {
      return null;
    }
    // Lazy-refresh rollingBps on read. Without this, callers that poll getState() would see
    // a frozen pre-stall speed until the next recordProgress() fires — but if the client is
    // truly stalled, recordProgress() never fires again. Only refresh for active statuses
    // where "recent throughput" is meaningful; PAUSED/COMPLETED/FAILED sessions intentionally
    // preserve their last computed rollingBps as a historical value.
    if (
      session.status === STATUS.UPLOADING ||
      session.status === STATUS.SLOW ||
      session.status === STATUS.STALLED ||
      session.status === STATUS.FINALIZING
    ) {
      applyRollingSpeed(session, Date.now());
    }
    return {
      uploadId,
      totalBytes: session.totalBytes,
      uploadedBytes: session.uploadedBytes,
      elapsedTime: session.elapsedTime,
      averageBps: session.averageBps,
      rollingBps: session.rollingBps,
      status: session.status,
    };
  },

  reset() {
    sessions.forEach((session) => clearStalledTimer(session));
    sessions.clear();
    listeners.clear();
    if (decayTickHandle !== null) {
      clearInterval(decayTickHandle);
      decayTickHandle = null;
    }
  },
};

export const getTelemetryDisplay = (status) => {
  if (status === STATUS.WAITING) {
    return {
      message: "Waiting for the previous upload to finish…",
      severity: "info",
    };
  }
  if (status === STATUS.PAUSED) {
    return {
      message: "Upload paused.",
      severity: "info",
    };
  }
  if (status === STATUS.SLOW) {
    return {
      message: "Upload speed is slower than expected.",
      severity: "warning",
    };
  }
  if (status === STATUS.STALLED) {
    return {
      message: "Upload appears stalled. Trying to resume…",
      severity: "danger",
    };
  }
  if (status === STATUS.FINALIZING) {
    return {
      message: "Files uploaded. Finishing package preparation…",
      severity: "info",
    };
  }
  return null;
};
